<template>
  <div id="sac-dropdown-container">
    <button
      type="button"
      :class="[
        'sac-dropdown',
        `cs-${field}`,
        label ? 'label' : '',
        show ? 'open' : '',
        transparent ? 'transparent-bg' : '',
        dark ? 'bg-95' : '',
        disable ? 'disabled' : '',
        small ? 'small' : '',
      ]"
      @click="toggleShow(!show)"
    >
      <span
        v-if="showValue()"
        :class="['sac-value', dark ? 'f-white' : '']"
      >
        {{ showLabel(value, options) }}
      </span>
      <span
        v-else-if="!label"
        class="sac-placeholder"
      >
        {{ placeholder }}
      </span>
      <span class="sac-arrow-container">
        <svg
          viewBox="0 0 24 24"
          :class="['arrow-down', show ? 'active' : '']"
        >
          <path
            :fill="[dark ? 'white' : 'black']"
            d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"
          />
        </svg>
      </span>
    </button>
    <span
      v-if="error"
      class="sac-error"
    >
      {{ error }}
    </span>
    <div
      :class="[
        show ? 'active' : '',
        'sac-option-container',
      ]"
    >
      <div class="sac-search-container">
        <input
          :ref="`cs-${field}-input-search`"
          v-model="searchValue"
          class="i-input"
          type="text"
          placeholder="Search"
          aria-label="Search"
        >
        <button
          v-if="searchValue.length > 0"
          type="button"
          class="sac-clear-button"
          @click.stop="clearInput"
        >
          <img
            class="clear-icon"
            src="@shared/assets/img/table/x.png"
            alt="clear"
          >
        </button>
      </div>
      <button
        v-for="(option, i) of filteredOptions"
        :key="i"
        type="button"
        class="sac-option"
        @click="selectOption(option)"
      >
        <span>{{ option[optionLabel] }}</span>
      </button>
    </div>
  </div>
</template>

<script>

export default {
  name: 'SACDropdown',
  props: {
    label: String,
    optionLabel: {
      type: String,
      default: 'label',
    },
    transparent: Boolean,
    dark: Boolean,
    optionValue: {
      type: String,
      default: 'value',
    },
    hideSearch: Boolean,
    searchParameter: {
      type: String,
      default: 'label',
    },
    field: String,
    options: Array,
    value: [String, Number, Boolean],
    change: Function,
    placeholder: String,
    error: String,
    passObject: {
      type: Boolean,
      default: false,
    },
    small: Boolean,
    disable: Boolean,
  },
  data() {
    return {
      show: false,
      searchValue: '',
    };
  },
  computed: {
    filteredOptions() {
      const {
        value,
        optionValue,
      } = this;
      let filteredOptions = this.options.filter((option) => option[optionValue] != value);
      if (this.searchValue) {
        filteredOptions = this.options.filter((c) => c[this.searchParameter]
          && c[this.searchParameter].toLowerCase().match(this.searchValue.toLowerCase()));
      }
      return filteredOptions;
    },
  },
  mounted() {
    document.querySelector(`:not(.cs-${this.field})`).addEventListener('click', this.closeOnClick);
  },
  destroyed() {
    document.querySelector(`:not(.cs-${this.field})`).removeEventListener('click', this.closeOnClick);
  },
  methods: {
    showLabel(v, options) {
      let l = v;
      options.forEach((o) => {
        if (o[this.optionValue] == v) {
          l = o[this.optionLabel];
        }
      });
      return l;
    },
    toggleShow(show) {
      if (!this.disable) {
        this.show = show;
      }
      if (show && this.$refs[`cs-${this.field}-input-search`]) {
        this.$refs[`cs-${this.field}-input-search`].focus();
      }
    },
    selectOption(v) {
      this.show = false;
      if (this.passObject) {
        this.change(this.field, v);
      } else {
        const val = v[this.optionValue];
        this.change(this.field, val);
      }
    },
    closeOnClick(e) {
      if (!e.target.closest(`.cs-${this.field}`)
        && this.show
        && !e.target.closest('.i-input')
        && !e.target.closest('.sac-clear-button')) {
        this.show = false;
      }
    },
    clearInput() {
      this.searchValue = '';
    },
    showValue() {
      let show = false;
      this.options.forEach((o) => {
        if (o[this.optionValue] == this.value) {
          show = true;
        }
      });
      return show;
    },
  },
};
</script>

<style lang="scss">

#sac-dropdown-container {
  position: relative;
  width: 100%;
  border-radius: 2px;

  .sac-dropdown {
    width: 100%;
    background-color: $NBLUE5;
    border-radius: 3px;
    font-weight: 500;
    cursor: pointer;
    display: grid;
    align-items: flex-end;
    justify-content: space-between;
    padding: 10px;
    &.small {
      align-items: center;
      padding: 5px 10px;
    }

    &.disabled {
      opacity: 0.6;
      pointer-events: none;
    }

    &.transparent-bg {
      background-color: transparent;
      border: 1px solid white;
    }

    &.bg-95 {
      background-color: $N95;
    }

    &.label {
      margin-top: 3px;
    }

    .sac-value {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &.f-white {
        color: $WHITE;
      }
    }

    .sac-label {
      position: absolute;
      left: 8px;
      bottom: 10px;
      color: $N30;
      transition: all .2s ease;

      &.active {
        color: $N100;
        transform: scale(.6) translate(-20px, -30px);
      }
    }

    &.open {
      border-radius: 3px;
    }

    .sac-placeholder {
      grid-column: 1;
      width: 100%;
      color: $N30;
      &.small {
        font-size: 12px;
      }
    }

    .sac-arrow-container {
      display: flex;
      align-items: center;
      height: 100%;
      width: 100%;
      grid-column: 2;
    }

    .arrow-down {
      height: 20px;
      width: 20px;

      &.active {
        transform: rotate(-180deg);
      }
    }
  }

  .sac-option-container {
    color: $N90;
    border-radius: 0 0 6px 6px;
    position: absolute;
    background: $WHITE;
    left: 0;
    right: 0;
    box-shadow: 0 1px 27px 12px rgb(9 15 32 / 10%);
    z-index: 1;
    max-height: 300px;
    overflow-y: auto;
    overflow-x: hidden;
    transform: scaleY(0);
    transform-origin: top;
    transition: transform 200ms ease;

    &.active {
      transform: scaleY(1);
    }

    .sac-option {
      color: $N90;
      padding: 6px 8px;
      cursor: pointer;
      user-select: none;
      width: 100%;
      text-align: left;

      &:not(:first-child) {
        border-top: 1px solid $N10;
      }

      &:hover {
        background-color: $NBLUE100;
        color: $WHITE;
      }
    }

    .i-input {
      padding: 8px;
      background: $NBLUE5;
      width: 100%;
    }

    .sac-search-container {
      position: sticky;
      top: 0;
      right: 0;
      left: 0;

      .sac-clear-button {
        position: absolute;
        display: flex;
        align-items: center;
        width: 30px;
        height: 30px;
        right: -2px;
        top: 4px;

        .clear-icon {
          width: 20px;
          height: 20px;
        }
      }
    }
  }

  .sac-error {
    color: $TOMATO100 !important;
    font-size: 0.875rem;
    font-weight: 500;
    padding-left: 4px;
    margin-top: 5px;
  }
}

.f-b-15 {
  color: black;
  font-weight: bold;
}
</style>
